import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { downloadInspectionReport, getReportErrorMessage } from '../utils/reportDownload';

const REPORT_OPTIONS = [
  { value: 'full', label: 'Full report', description: 'Summary and item lists for the whole register.' },
  { value: 'area', label: 'Per area report', description: 'The full report for one selected area.' },
  { value: 'failed', label: 'Failed items only', description: 'Saved inspection questions, photo, notes and details for each currently failed item.' },
];

export default function ReportDialog({ open, onClose, areas = [] }) {
  const [mode, setMode] = useState('full');
  const [selectedArea, setSelectedArea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [progress, setProgress] = useState(null);
  const pending = useRef(null);

  useEffect(() => {
    if (open) {
      setMode('full');
      setSelectedArea(null);
      setError('');
      setNotice('');
    } else {
      pending.current?.abort();
      pending.current = null;
      setLoading(false);
      setCancelling(false);
      setProgress(null);
    }
  }, [open]);

  useEffect(() => () => {
    pending.current?.abort();
    pending.current = null;
  }, []);

  const hasSelectedArea = selectedArea !== null && areas.some((area) => area.name === selectedArea);
  const canGenerate = !loading && (mode !== 'area' || hasSelectedArea);
  const close = () => {
    if (!pending.current) onClose();
  };

  const cancel = () => {
    if (!pending.current) {
      onClose();
      return;
    }
    setCancelling(true);
    pending.current.abort();
  };

  const generate = async () => {
    if (pending.current || !canGenerate) return;
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError('');
    setNotice('');
    setProgress({ stage: 'queued', progress: 0 });
    try {
      await downloadInspectionReport({
        mode,
        area: selectedArea,
        signal: controller.signal,
        onProgress: (update) => {
          if (pending.current === controller) setProgress(update);
        },
      });
      if (pending.current === controller) onClose();
    } catch (failure) {
      if (failure.name === 'AbortError' || failure.code === 'ERR_CANCELED') {
        if (pending.current === controller) setNotice('Report generation cancelled.');
      } else {
        const message = await getReportErrorMessage(failure);
        if (pending.current === controller) setError(message);
      }
    } finally {
      if (pending.current === controller) {
        pending.current = null;
        setLoading(false);
        setCancelling(false);
        setProgress(null);
      }
    }
  };

  const stageLabels = {
    queued: 'Waiting to generate your report…',
    preparing: 'Preparing report data…',
    rendering: 'Generating report pages…',
    merging: 'Combining report pages…',
    saving: 'Preparing download…',
    downloading: 'Downloading your PDF…',
    completed: 'Report ready.',
  };
  const progressLabel = cancelling
    ? 'Cancelling report generation…'
    : stageLabels[progress?.stage] || progress?.message || 'Generating your report…';
  const percentage = Math.round(Math.max(0, Math.min(100, Number(progress?.progress) || 0)));

  return (
    <Dialog
      open={open}
      onClose={close}
      disableEscapeKeyDown={loading}
      fullWidth
      maxWidth="sm"
      aria-labelledby="report-dialog-title"
      PaperProps={{ sx: {
        m: { xs: 2, sm: 4 },
        width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
      } }}
    >
      <DialogTitle id="report-dialog-title" sx={{ fontWeight: 700 }}>Generate report</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choose what to include in your PDF.</Typography>
        <RadioGroup
          name="report-mode"
          aria-label="Report type"
          value={mode}
          onChange={(event) => { setMode(event.target.value); setError(''); }}
          sx={{ gap: 1 }}
        >
          {REPORT_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={loading}
              control={<Radio />}
              sx={{
                m: 0, p: 1, alignItems: 'flex-start', border: '1px solid', borderRadius: 1.5,
                borderColor: mode === option.value ? 'primary.main' : 'divider',
                backgroundColor: mode === option.value ? 'action.selected' : 'transparent',
                '& .MuiFormControlLabel-label': { py: 0.75 },
              }}
              label={(
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{option.description}</Typography>
                </Box>
              )}
            />
          ))}
        </RadioGroup>

        {mode === 'area' && (
          <Box sx={{ mt: 2 }}>
            <Typography id="report-area-label" variant="subtitle2" sx={{ mb: 1 }}>Choose an area</Typography>
            {areas.length ? (
              <RadioGroup
                name="report-area"
                aria-labelledby="report-area-label"
                value={selectedArea}
                onChange={(event) => { setSelectedArea(event.target.value); setError(''); }}
                sx={{ maxHeight: 240, overflowY: 'auto', flexWrap: 'nowrap', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                {areas.map((area) => (
                  <FormControlLabel
                    key={area.name}
                    value={area.name}
                    disabled={loading}
                    control={<Radio size="small" />}
                    sx={{ m: 0, px: 0.75, py: 0.25, flexShrink: 0, '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{area.name.trim() ? area.name : '(Unnamed area)'}</Typography>
                        <Chip size="small" label={area.count ?? 0} aria-label={`${area.count ?? 0} items`} sx={{ flexShrink: 0 }} />
                      </Box>
                    )}
                  />
                ))}
              </RadioGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No areas found in this register.</Typography>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {notice && <Alert severity="info" sx={{ mt: 2 }}>{notice}</Alert>}
        {loading && (
          <Box sx={{ mt: 2 }}>
            <Box role="status" aria-live="polite" aria-atomic="true" sx={{ mb: 1 }}>
              <Typography id="report-progress-label" variant="body2">{progressLabel} {percentage}%</Typography>
              {progress?.totalBatches > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {progress.completedBatches || 0} of {progress.totalBatches} batches complete
                  {progress.totalItems > 0 && Number.isFinite(progress.completedItems)
                    ? ` · ${progress.completedItems} of ${progress.totalItems} items processed`
                    : ''}
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              aria-labelledby="report-progress-label"
              aria-valuetext={`${progressLabel} ${percentage}%`}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={cancel} disabled={cancelling}>{cancelling ? 'Cancelling…' : loading ? 'Cancel generation' : 'Cancel'}</Button>
        <Button variant="contained" onClick={generate} disabled={!canGenerate} sx={{ whiteSpace: 'nowrap' }}>{loading ? 'Generating…' : 'Generate PDF'}</Button>
      </DialogActions>
    </Dialog>
  );
}
